import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'

const Privacy = () => {
    return (
        <>
            <Helmet>
                <title>Privacy Policy | MyMargdarshak</title>
                <meta name="description" content="Read MyMargdarshak's privacy policy to understand how we collect, use, and protect your personal information." />
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 flex flex-col pt-0">
                {/* Modern Professional Legal Hero */}
                <section className="relative pt-28 lg:pt-36 pb-16 lg:pb-24 overflow-hidden bg-white border-b border-gray-100">
                    {/* Subtle Background Pattern */}
                    <div className="absolute inset-0 z-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
                    
                    {/* Floating Design Element */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none overflow-hidden">
                        <div className="absolute top-[-20%] right-[-10%] w-[50%] h-[50%] bg-blue-50 rounded-full blur-[100px] opacity-60"></div>
                        <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-50 rounded-full blur-[80px] opacity-60"></div>
                    </div>

                    <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="max-w-4xl mx-auto text-center">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                className="inline-flex items-center space-x-2 bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full mb-6 border border-blue-100 italic"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                <span className="text-xs font-black uppercase tracking-widest">Legal Document</span>
                            </motion.div>

                            <motion.h1 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="text-4xl sm:text-6xl font-black mb-6 text-gray-900 leading-tight tracking-tight"
                            >
                                Privacy <span className="text-primary-600">Policy</span>
                            </motion.h1>

                            <motion.p 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto font-medium"
                            >
                                Learn about how we collect, use, and safeguard your personal information.
                            </motion.p>
                        </div>
                    </div>
                </section>

                {/* Content Section */}
                <section className="py-16">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="card p-8"
                        >
                            <div className="prose prose-lg max-w-none">
                                <p className="text-sm text-gray-500 mb-8">
                                    Last updated: {new Date().toLocaleDateString()}
                                </p>

                                <h2>1. Information We Collect</h2>
                                <p>
                                    We collect information you provide directly to us, such as when you create an account,
                                    submit an enquiry, or contact us for support.
                                </p>

                                <h3>Personal Information</h3>
                                <ul>
                                    <li>Name and contact information</li>
                                    <li>Email address and phone number</li>
                                    <li>Educational background and interests</li>
                                    <li>Communication preferences</li>
                                </ul>

                                <h3>Automatically Collected Information</h3>
                                <ul>
                                    <li>Device and browser information</li>
                                    <li>IP address and location data</li>
                                    <li>Usage patterns and preferences</li>
                                    <li>Cookies and similar technologies</li>
                                </ul>

                                <h2>2. How We Use Your Information</h2>
                                <p>We use the information we collect to:</p>
                                <ul>
                                    <li>Provide and improve our services</li>
                                    <li>Respond to your enquiries and requests</li>
                                    <li>Send you relevant information about courses and programs</li>
                                    <li>Analyze usage patterns to enhance user experience</li>
                                    <li>Comply with legal obligations</li>
                                </ul>

                                <h2>3. Information Sharing</h2>
                                <p>
                                    We do not sell, trade, or otherwise transfer your personal information to third parties
                                    without your consent, except as described in this policy.
                                </p>

                                <h3>We may share information with:</h3>
                                <ul>
                                    <li>Partner universities and educational institutions</li>
                                    <li>Service providers who assist in our operations</li>
                                    <li>Legal authorities when required by law</li>
                                    <li>Business partners with your explicit consent</li>
                                </ul>

                                <h2>4. Data Security</h2>
                                <p>
                                    We implement appropriate security measures to protect your personal information against
                                    unauthorized access, alteration, disclosure, or destruction.
                                </p>

                                <h2>5. Your Rights</h2>
                                <p>You have the right to:</p>
                                <ul>
                                    <li>Access and update your personal information</li>
                                    <li>Request deletion of your data</li>
                                    <li>Opt-out of marketing communications</li>
                                    <li>Request data portability</li>
                                </ul>

                                <h2>6. Cookies and Tracking</h2>
                                <p>
                                    We use cookies and similar technologies to enhance your browsing experience and
                                    analyze website traffic. You can control cookie settings through your browser.
                                </p>

                                <h2>7. Third-Party Links</h2>
                                <p>
                                    Our website may contain links to third-party websites. We are not responsible for
                                    the privacy practices of these external sites.
                                </p>

                                <h2>8. Children's Privacy</h2>
                                <p>
                                    Our services are not intended for children under 13. We do not knowingly collect
                                    personal information from children under 13.
                                </p>

                                <h2>9. Changes to This Policy</h2>
                                <p>
                                    We may update this privacy policy from time to time. We will notify you of any
                                    material changes by posting the new policy on this page.
                                </p>

                                <h2>10. Contact Us</h2>
                                <p>
                                    If you have any questions about this privacy policy, please contact us at:
                                </p>
                                <ul>
                                    <li>Email: admission@mymargdarshak.com</li>
                                    <li>Phone: +91-9810530354</li>
                                    <li>Address: Lal Kuah, Ghaziabad, Uttar Pradesh, India</li>
                                </ul>
                            </div>
                        </motion.div>
                    </div>
                </section>
            </div>
        </>
    )
}

export default Privacy
